import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

/**
 * Generic virtualized list: renders only the rows currently on screen, so a list
 * of thousands scrolls as smoothly as a handful. Owns its own scroll container.
 * Callers supply the row renderer and a stable key — the virtualizer scaffold
 * (measure, overscan, absolute positioning) lives here exactly once.
 */
export function VirtualRowList<T>({
  items,
  rowHeight,
  keyOf,
  renderRow,
  emptyText,
  overscan = 8,
}: {
  /** Full, already-filtered dataset (virtualizer renders only the visible slice). */
  items: T[];
  /** Fixed row height in px (must match the rendered row). */
  rowHeight: number;
  /** Stable key for an item (used for React keys + virtualizer identity). */
  keyOf: (item: T, index: number) => string | number;
  /** Renders a single row. */
  renderRow: (item: T) => React.ReactNode;
  /** Shown when the list is empty (e.g. loading or "no results"). */
  emptyText: string;
  /** Rows rendered above/below the viewport for smooth scroll. */
  overscan?: number;
}): React.JSX.Element {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
    getItemKey: (index) => {
      const item = items[index];
      return item === undefined ? index : keyOf(item, index);
    },
  });

  if (items.length === 0) {
    return <p className="p-4 text-center text-xs text-muted-foreground">{emptyText}</p>;
  }

  return (
    <div ref={parentRef} className="min-h-0 flex-1 overflow-y-auto">
      <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
        {virtualizer.getVirtualItems().map((row) => {
          const item = items[row.index];
          if (item === undefined) return null;
          return (
            <div
              key={row.key}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${row.start}px)` }}
            >
              {renderRow(item)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
